/* ============================================================================
   ALIA · VELVET — 03-velvet.js

   ONE file, loaded with `defer`, for the whole theme. Do not fragment this
   per-section: the choreography is orchestrated globally by a SINGLE Lenis
   instance synced to a SINGLE ScrollTrigger registry that spans every section.

   Section awareness
   -----------------
   Every timeline, ScrollTrigger, matchMedia, interval, timeout,
   IntersectionObserver, rAF loop and SplitText instance is recorded against the
   id of the Shopify section that owns it. `shopify:section:unload` kills exactly
   that section's own work and nothing else; `shopify:section:load` rebuilds it,
   re-binds the cross-section wiring and calls ScrollTrigger.refresh() so pin
   spacing survives injection and reordering in the theme editor.

   The global scroll system (Lenis + ScrollTrigger sync, custom cursor, nav and
   progress bar, anchor scrolling, easter egg) is created ONCE and never torn
   down, because it does not belong to any single section.
   ========================================================================== */

document.documentElement.classList.add('js');

(function () {
  "use strict";

  /* ── capability + preference detection (once) ───────────────────────────── */
  var RM = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var COARSE = window.matchMedia && window.matchMedia('(pointer:coarse)').matches;
  var hasGSAP = typeof window.gsap !== 'undefined';
  var hasST = hasGSAP && typeof window.ScrollTrigger !== 'undefined';
  var hasSplit = hasGSAP && typeof window.SplitText !== 'undefined';
  var hasLenis = typeof window.Lenis !== 'undefined';
  var DPR = Math.min(window.devicePixelRatio || 1, 2);

  var $ = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function pad2(n) { return ('0' + Math.floor(n)).slice(-2); }
  function num(v, fallback) { var n = parseInt(v, 10); return isFinite(n) ? n : fallback; }

  if (hasST) { gsap.registerPlugin(ScrollTrigger); }
  if (hasSplit) { try { gsap.registerPlugin(SplitText); } catch (e) {} }

  /* ══════════════════════════════════════════════════════════════════════════
     PER-SECTION LIFECYCLE REGISTRY
     ══════════════════════════════════════════════════════════════════════ */

  function Scope(id) {
    this.id = id;
    this._st = []; this._tw = []; this._mm = []; this._iv = [];
    this._to = []; this._io = []; this._raf = []; this._ev = []; this._sp = [];
    this._cb = [];
  }
  Scope.prototype.cleanup = function (fn) { this._cb.push(fn); };
  Scope.prototype.st = function (t) { if (t) this._st.push(t); return t; };
  Scope.prototype.tw = function (t) {
    if (t) { this._tw.push(t); if (t.scrollTrigger) this._st.push(t.scrollTrigger); }
    return t;
  };
  Scope.prototype.mm = function (m) { if (m) this._mm.push(m); return m; };
  Scope.prototype.iv = function (fn, ms) { var i = setInterval(fn, ms); this._iv.push(i); return i; };
  Scope.prototype.to = function (fn, ms) { var t = setTimeout(fn, ms); this._to.push(t); return t; };
  Scope.prototype.io = function (o) { if (o) this._io.push(o); return o; };
  Scope.prototype.raf = function (set) { this._raf.push(set); return set; };
  Scope.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this._ev.push([target, type, fn, opts]);
  };
  Scope.prototype.split = function (s) { if (s) this._sp.push(s); return s; };
  Scope.prototype.destroy = function () {
    var i;
    for (i = 0; i < this._mm.length; i++) { try { this._mm[i].revert(); } catch (e) {} }
    for (i = 0; i < this._st.length; i++) { try { this._st[i].kill(true); } catch (e) {} }
    for (i = 0; i < this._tw.length; i++) { try { this._tw[i].kill(); } catch (e) {} }
    for (i = 0; i < this._sp.length; i++) { try { this._sp[i].revert(); } catch (e) {} }
    for (i = 0; i < this._iv.length; i++) { clearInterval(this._iv[i]); }
    for (i = 0; i < this._to.length; i++) { clearTimeout(this._to[i]); }
    for (i = 0; i < this._io.length; i++) { try { this._io[i].disconnect(); } catch (e) {} }
    for (i = 0; i < this._raf.length; i++) { this._raf[i].stopped = true; if (this._raf[i].id) cancelAnimationFrame(this._raf[i].id); }
    for (i = 0; i < this._ev.length; i++) {
      try { this._ev[i][0].removeEventListener(this._ev[i][1], this._ev[i][2], this._ev[i][3]); } catch (e) {}
    }
    for (i = 0; i < this._cb.length; i++) { try { this._cb[i](); } catch (e) {} }
    this._st = []; this._tw = []; this._mm = []; this._iv = [];
    this._to = []; this._io = []; this._raf = []; this._ev = []; this._sp = [];
    this._cb = [];
  };

  var scopes = {};
  function scopeFor(id) {
    if (!scopes[id]) scopes[id] = new Scope(id);
    return scopes[id];
  }
  function destroyScope(id) {
    if (scopes[id]) { scopes[id].destroy(); delete scopes[id]; }
  }

  /* shared app state that crosses section boundaries */
  var app = {
    booted: false,
    preloaderDone: false,
    heroStart: null,
    lenis: null,
    lenisWired: false,
    nav: null,
    prog: null
  };

  function maybeStartHero() {
    if (app.preloaderDone && app.heroStart) app.heroStart();
  }

  function qsaIn(root, sel) {
    var out = [], i;
    if (root.nodeType === 1 && root.matches && root.matches(sel)) out.push(root);
    var found = root.querySelectorAll ? root.querySelectorAll(sel) : [];
    for (i = 0; i < found.length; i++) out.push(found[i]);
    return out;
  }

  /* ══════════════════════════════════════════════════════════════════════════
     GLOBAL SCROLL SYSTEM — created once, never torn down.
     This wiring block must stay intact and must run FIRST.
     ══════════════════════════════════════════════════════════════════════ */

  function initSmoothScroll() {
    if (app.lenisWired) return;
    app.lenisWired = true;
    if (!(hasLenis && !RM)) return;
    try {
      app.lenis = new Lenis({ duration: 1.15, smoothWheel: true, lerp: .1 });
      if (hasST) {
        app.lenis.on('scroll', ScrollTrigger.update);
        gsap.ticker.add(function (t) { app.lenis.raf(t * 1000); });
        gsap.ticker.lagSmoothing(0);
      } else {
        (function rafl(t) { app.lenis.raf(t); requestAnimationFrame(rafl); })();
      }
    } catch (e) { app.lenis = null; }
  }

  /* Anchor smooth scroll — delegated, so sections added by the editor are
     covered without re-binding and without stacking duplicate handlers.

     TRAP D: #floor and friends only exist on the index, so the nav writes them
     as "/#floor" and they work from every template. Here we resolve the href
     against the current URL: same path means scroll, different path means let
     the browser navigate home and land on the id. A bare "#floor" still works
     because it resolves to the current path. */
  function initAnchors() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      var raw = a.getAttribute('href');
      if (!raw || raw.indexOf('#') === -1) return;
      if (a.target === '_blank') return;
      var url;
      try { url = new URL(a.href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname !== window.location.pathname) return;   /* let it navigate */
      var id = url.hash;
      if (!id || id.length < 2) return;
      var el;
      try { el = document.querySelector(id); } catch (err) { return; }
      if (!el) return;
      e.preventDefault();
      if (app.lenis) { app.lenis.scrollTo(el, { offset: -6, duration: 1.2 }); }
      else { el.scrollIntoView({ behavior: RM ? 'auto' : 'smooth' }); }
    });
  }

  /* nav shrink + scroll progress. #nav and #prog live in two different
     sections, so the element refs are re-resolved on every section load. */
  function rebindChrome() {
    app.nav = document.getElementById('nav');
    app.prog = document.getElementById('prog');
  }
  function initNavProgress() {
    function onScroll() {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      if (app.nav) { if (y > 60) app.nav.classList.add('shrunk'); else app.nav.classList.remove('shrunk'); }
      if (app.prog) {
        var h = document.documentElement.scrollHeight - window.innerHeight;
        app.prog.style.width = (h > 0 ? (y / h * 100) : 0) + '%';
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    rebindChrome();
    onScroll();
  }

  /* custom ember cursor — one rAF loop for the life of the page */
  function initCursor() {
    if (COARSE) return;
    var mx = window.innerWidth / 2, my = window.innerHeight / 2, rx = mx, ry = my;
    window.addEventListener('mousemove', function (e) {
      mx = e.clientX; my = e.clientY;
      var dot = document.querySelector('.cur-dot');
      if (dot) dot.style.transform = 'translate(' + mx + 'px,' + my + 'px)';
    }, { passive: true });
    (function raf() {
      rx = lerp(rx, mx, .18); ry = lerp(ry, my, .18);
      var ring = document.querySelector('.cur');
      if (ring) ring.style.transform = 'translate(' + rx + 'px,' + ry + 'px)';
      requestAnimationFrame(raf);
    })();
    /* #alia-wgt-host is the booking widget's shadow host: events from inside a
       shadow root retarget to the host, so listing it here is what keeps the
       ember cursor reacting to the widget's buttons the way it does to the
       theme's own. The host is pointer-events:none except over its controls. */
    var hot = 'a,button,.zone,.dish,.tier,.inq-btn,.faq-q,.magnet,#alia-wgt-host';
    document.addEventListener('mouseover', function (e) { if (e.target.closest && e.target.closest(hot)) { document.body.classList.add('cur-hot'); } });
    document.addEventListener('mouseout', function (e) { if (e.target.closest && e.target.closest(hot)) { document.body.classList.remove('cur-hot'); } });
  }

  /* easter egg: type ALIA for an ember flare */
  function initEasterEgg() {
    var buf = '';
    window.addEventListener('keydown', function (e) {
      if (!e.key || e.key.length !== 1) return;
      buf = (buf + e.key.toLowerCase()).slice(-4);
      if (buf === 'alia') {
        var flare = document.createElement('div');
        flare.style.cssText = 'position:fixed;inset:0;z-index:9997;pointer-events:none;background:radial-gradient(50% 50% at 50% 60%,rgba(138,95,58,0.5),transparent 60%);opacity:0';
        document.body.appendChild(flare);
        if (hasGSAP) {
          gsap.to(flare, { opacity: 1, duration: .5, ease: 'power2.out', onComplete: function () {
            gsap.to(flare, { opacity: 0, duration: 1.4, ease: 'power2.in', onComplete: function () { flare.remove(); } });
          } });
        } else { setTimeout(function () { flare.remove(); }, 1500); }
      }
    });
  }

  /* ══════════════════════════════════════════════════════════════════════════
     SHARED PER-SECTION REVEALS
     ══════════════════════════════════════════════════════════════════════ */

  function initReveals(root, sc) {
    var els = qsaIn(root, '[data-reveal]');
    if (hasST && !RM) {
      els.forEach(function (el) {
        sc.tw(gsap.to(el, {
          opacity: 1, y: 0, duration: .95, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 86%', once: true }
        }));
      });
    } else {
      els.forEach(function (el) { el.style.opacity = 1; el.style.transform = 'none'; });
    }

    /* headline char cascade for .h2 blocks via SplitText */
    if (hasSplit && hasST && !RM) {
      qsaIn(root, '.h2').forEach(function (h) {
        if (h.__aliaSplit) return;
        h.__aliaSplit = true;
        try {
          var sp = new SplitText(h, { type: 'chars' });
          sc.split(sp);
          gsap.set(sp.chars, { yPercent: 110, opacity: 0 });
          sc.tw(gsap.to(sp.chars, {
            yPercent: 0, opacity: 1, duration: .8, ease: 'power4.out', stagger: .022,
            scrollTrigger: { trigger: h, start: 'top 88%', once: true }
          }));
        } catch (e) {}
      });
    }
  }

  /* word-mask rise used by the manifesto and the velvet interlude */
  function wordRise(host, sc) {
    if (!host) return;
    var words = $$('.word i', host);
    if (!(hasGSAP && !RM)) { words.forEach(function (w) { w.style.transform = 'none'; }); return; }
    gsap.set(words, { yPercent: 120 });
    sc.tw(gsap.to(words, {
      yPercent: 0, duration: 1.05, ease: 'power4.out', stagger: .06,
      scrollTrigger: { trigger: host, start: 'top 82%', once: true }
    }));
  }

  /* ══════════════════════════════════════════════════════════════════════════
     SECTION MODULES
     ══════════════════════════════════════════════════════════════════════ */

  /* ---------- PRELOADER ---------------------------------------------------- */
  function initPreloader(pre, sc) {
    /* TRAP: if the editor re-injects this section after the page has already
       booted, a full-screen overlay would reappear and cover the live site.
       Dismiss it on sight instead of replaying the intro. */
    if (app.booted) {
      pre.classList.add('gone');
      if (pre.parentNode) pre.parentNode.removeChild(pre);
      app.preloaderDone = true;
      maybeStartHero();
      if (hasST) ScrollTrigger.refresh();
      return;
    }

    var bar = $('.pre-bar i', pre), pct = $('.pre-pct', pre);
    var letters = $$('.pre-logo span', pre), sub = $('.pre-sub', pre);
    var p = 0, done = false;

    if (hasGSAP && !RM) {
      sc.tw(gsap.to(letters, { opacity: 1, y: 0, duration: .7, stagger: .11, ease: 'power3.out' }));
      sc.tw(gsap.to(sub, { opacity: 1, duration: .6, delay: .55 }));
    } else {
      letters.forEach(function (m) { m.style.opacity = 1; m.style.transform = 'none'; });
      if (sub) sub.style.opacity = 1;
    }

    var iv = sc.iv(function () {
      p += Math.random() * 17 + 9; if (p >= 100) { p = 100; }
      if (bar) bar.style.width = p + '%';
      if (pct) pct.textContent = pad2(p);
      if (p >= 100 && !done) { done = true; clearInterval(iv); sc.to(finish, 300); }
    }, 120);

    function finish() {
      pre.classList.add('gone');
      app.preloaderDone = true;
      maybeStartHero();
      sc.to(function () {
        if (pre && pre.parentNode) { pre.parentNode.removeChild(pre); }
        if (hasST) ScrollTrigger.refresh();
      }, 760);
    }

    sc.to(function () { if (!done) { done = true; clearInterval(iv); finish(); } }, 2300);
  }

  /* ---------- MOBILE NAV DRAWER -------------------------------------------- */
  function initDrawer(drawer, sc) {
    var host = drawer.parentNode || document;
    var burger = host.querySelector('.nav-burger');
    var scrim = host.querySelector('.nav-scrim');
    if (!burger) return;
    var lastFocus = null;

    function isOpen() { return drawer.classList.contains('open'); }

    function setOpen(on) {
      drawer.classList.toggle('open', on);
      if (scrim) scrim.classList.toggle('open', on);
      burger.classList.toggle('on', on);
      burger.setAttribute('aria-expanded', on ? 'true' : 'false');
      drawer.setAttribute('aria-hidden', on ? 'false' : 'true');
      document.body.classList.toggle('nav-open', on);
      if (on) {
        lastFocus = document.activeElement;
        /* park the smooth-scroll rather than locking body overflow — locking it
           while Lenis is running makes the page jump to the top on iOS */
        if (app.lenis && app.lenis.stop) app.lenis.stop();
        var f = drawer.querySelector('a');
        if (f) setTimeout(function () { f.focus(); }, 60);
      } else {
        if (app.lenis && app.lenis.start) app.lenis.start();
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }
    }

    sc.on(burger, 'click', function () { setOpen(!isOpen()); });
    if (scrim) sc.on(scrim, 'click', function () { setOpen(false); });

    /* Taking a link closes the drawer. The global delegated anchor handler
       still performs the Lenis scroll, so in-page anchors keep working. */
    sc.on(drawer, 'click', function (e) {
      if (e.target.closest && e.target.closest('a')) setOpen(false);
    });

    sc.on(document, 'keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab') return;
      var f = drawer.querySelectorAll('a,button');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      var active = document.activeElement;
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    });

    /* intentionally no auto-close on resize — drawer is available at all widths */

    /* leave nothing behind if the editor swaps this section out mid-open */
    sc.cleanup(function () {
      document.body.classList.remove('nav-open');
      if (app.lenis && app.lenis.start) app.lenis.start();
    });
  }

  /* ---------- CORPORATE GALLERY VIEWER ------------------------------------- */
  function initLightbox(root, sc) {
    var shots = $('.gshot', root);
    if (!shots.length) return;

    /* Appended to <body>, not inside the section: the cards are transformed by
       the reveal animation, and a position:fixed element inside a transformed
       ancestor is positioned against that ancestor instead of the viewport. */
    var lb = document.createElement('div');
    lb.className = 'lb';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Photograph viewer');
    lb.innerHTML =
      '<button class="lb-close" type="button" aria-label="Close viewer">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
      '<button class="lb-prev" type="button" aria-label="Previous photograph">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg></button>' +
      '<button class="lb-next" type="button" aria-label="Next photograph">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg></button>' +
      '<div class="lb-stage">' +
        '<img class="lb-img" alt="">' +
        '<p class="lb-cap"></p>' +
        '<span class="lb-count"></span>' +
      '</div>';
    document.body.appendChild(lb);

    var img = lb.querySelector('.lb-img');
    var cap = lb.querySelector('.lb-cap');
    var cnt = lb.querySelector('.lb-count');
    var btnClose = lb.querySelector('.lb-close');
    var btnPrev = lb.querySelector('.lb-prev');
    var btnNext = lb.querySelector('.lb-next');
    var single = shots.length < 2;
    if (single) { btnPrev.hidden = true; btnNext.hidden = true; }

    var idx = 0, opener = null;
    function isOpen() { return lb.classList.contains('is-open'); }

    function show(i) {
      /* wrap, so the arrows never dead-end on the first or last picture */
      idx = (i + shots.length) % shots.length;
      var el = shots[idx];
      var caption = el.getAttribute('data-cap') || '';
      img.setAttribute('src', el.getAttribute('data-full') || '');
      /* the caption is visible text describing the same picture, so repeating
         it as alt would have a screen reader read it twice */
      img.setAttribute('alt', caption ? '' : 'Photograph');
      cap.textContent = caption;
      cap.hidden = !caption;
      cnt.textContent = single ? '' : (idx + 1) + ' / ' + shots.length;
    }

    function open(i) {
      opener = shots[i];
      show(i);
      lb.classList.add('is-open');
      /* park the smooth scroll rather than locking body overflow — locking it
         while Lenis runs jumps the page to the top on iOS. Same choice as the
         drawer, deliberately, so there is one way of doing this in the theme. */
      if (app.lenis && app.lenis.stop) app.lenis.stop();
      sc.to(function () { btnClose.focus(); }, 60);
    }

    function close() {
      if (!isOpen()) return;
      lb.classList.remove('is-open');
      if (app.lenis && app.lenis.start) app.lenis.start();
      /* releasing the src frees the full-size decode; the browser keeps it
         cached, so reopening is still instant */
      sc.to(function () { if (!isOpen()) img.removeAttribute('src'); }, 340);
      if (opener && opener.focus) opener.focus();
      opener = null;
    }

    shots.forEach(function (el, i) {
      sc.on(el, 'click', function () { open(i); });
    });

    sc.on(btnClose, 'click', close);
    sc.on(btnPrev, 'click', function () { show(idx - 1); });
    sc.on(btnNext, 'click', function () { show(idx + 1); });

    /* the backdrop closes, the picture and the controls do not */
    sc.on(lb, 'click', function (e) {
      if (e.target === lb || e.target === lb.querySelector('.lb-stage')) close();
    });

    sc.on(document, 'keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') { close(); return; }
      if (!single && e.key === 'ArrowLeft') { e.preventDefault(); show(idx - 1); return; }
      if (!single && e.key === 'ArrowRight') { e.preventDefault(); show(idx + 1); return; }
      if (e.key !== 'Tab') return;
      /* focus stays inside the dialog while it is open */
      var f = [].slice.call(lb.querySelectorAll('button')).filter(function (b) { return !b.hidden; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1], active = document.activeElement;
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    });

    sc.cleanup(function () {
      if (app.lenis && app.lenis.start) app.lenis.start();
      if (lb.parentNode) lb.parentNode.removeChild(lb);
    });
  }

  /* ---------- HERO --------------------------------------------------------- */
  function initHero(hero, sc) {
    buildHeroTitle(hero);
    initEmberCanvas(hero, sc);
    initDoorsCountdown(hero, sc);

    var started = false;
    function start() {
      if (started) return; started = true;
      var chars = $$('#heroTitle .ch', hero);
      if (hasGSAP && !RM) {
        gsap.set(chars, { yPercent: 118, opacity: 0 });
        var tl = sc.tw(gsap.timeline());
        tl.to(chars, { yPercent: 0, opacity: 1, duration: 1, ease: 'power4.out', stagger: .032 }, 0)
          .to($$('.hero-sub', hero), { opacity: 1, duration: .9, ease: 'power2.out' }, .45)
          .to($$('.hero-meta .m', hero), { opacity: 1, y: 0, duration: .7, ease: 'power3.out', stagger: .09 }, .6)
          .to($$('.hero-foot', hero), { opacity: 1, duration: .8 }, .8);
      } else {
        chars.forEach(function (c) { c.style.opacity = 1; c.style.transform = 'none'; });
        $$('.hero-sub,.hero-meta .m,.hero-foot', hero).forEach(function (e) { e.style.opacity = 1; e.style.transform = 'none'; });
      }
    }

    app.heroStart = start;
    /* no preloader on the page (or it has already been removed) → start now */
    if (!document.getElementById('pre')) app.preloaderDone = true;
    maybeStartHero();
  }

  function buildHeroTitle(hero) {
    var host = $('#heroTitle', hero);
    if (!host) return;
    var l1 = $('.l1', host), l2 = $('.l2', host);
    if (!l1 || !l2) return;
    function fill(el, txt, amberFrom) {
      var frag = '';
      for (var i = 0; i < txt.length; i++) {
        var ch = txt[i];
        if (ch === ' ') { frag += '<span class="ch" style="width:.3em">&nbsp;</span>'; continue; }
        var cls = (amberFrom != null && i >= amberFrom) ? 'ch amberword' : 'ch';
        frag += '<span class="' + cls + '">' + ch + '</span>';
      }
      el.innerHTML = frag;
    }
    fill(l1, host.getAttribute('data-l1') || 'VELVET', null);
    fill(l2, host.getAttribute('data-l2') || 'THE TABLE IS THE NIGHT', null);
  }

  /* ember + candle particle canvas — IntersectionObserver-gated, DPR-capped,
     drawn from buffer dims. The gating IS the 60fps budget: do not remove it. */
  function initEmberCanvas(hero, sc) {
    var cv = $('#emberCanvas', hero);
    if (!cv || RM) return;
    var ctx = cv.getContext('2d');
    if (!ctx) return;

    var W = 0, H = 0, parts = [], running = false;
    var loop = sc.raf({ id: 0, stopped: false });

    function size() {
      var r = cv.getBoundingClientRect();
      cv.width = Math.max(1, Math.round(r.width * DPR));
      cv.height = Math.max(1, Math.round(r.height * DPR));
      W = cv.width; H = cv.height;
    }
    function mk() {
      return {
        x: Math.random() * W, y: H + Math.random() * H * 0.3, r: (Math.random() * 2.2 + 0.6) * DPR,
        vy: -(Math.random() * 0.5 + 0.2) * DPR, vx: (Math.random() - 0.5) * 0.3 * DPR,
        life: 0, max: Math.random() * 260 + 160, tw: Math.random() * 6.28
      };
    }
    function init() {
      parts = [];
      var n = Math.round((W * H) / (46000 * DPR)); n = clamp(n, 26, 90);
      for (var i = 0; i < n; i++) { var p = mk(); p.y = Math.random() * H; p.life = Math.random() * p.max; parts.push(p); }
    }
    function frame() {
      if (!running || loop.stopped) return;
      ctx.clearRect(0, 0, W, H);
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        p.life++; p.tw += 0.05;
        p.y += p.vy; p.x += p.vx + Math.sin(p.tw) * 0.25 * DPR;
        if (p.life > p.max || p.y < -20) { parts[i] = mk(); continue; }
        var t = p.life / p.max;
        var a = Math.sin(t * Math.PI) * 0.8 * (0.6 + 0.4 * Math.sin(p.tw));
        var g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 3.2);
        g.addColorStop(0, 'rgba(238,237,227,' + (a * 0.5) + ')');
        g.addColorStop(0.4, 'rgba(138,95,58,' + (a * 0.55) + ')');
        g.addColorStop(1, 'rgba(138,95,58,0)');
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 3.2, 0, 6.2832); ctx.fill();
      }
      loop.id = requestAnimationFrame(frame);
    }

    size(); init();
    sc.io(new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (e.isIntersecting) { if (!running && !loop.stopped) { running = true; frame(); } }
        else { running = false; if (loop.id) cancelAnimationFrame(loop.id); }
      });
    }, { threshold: 0.02})).observe(cv);

    var rt;
    sc.on(window, 'resize', function () {
      clearTimeout(rt);
      rt = sc.to(function () { size(); init(); }, 200);
    });
  }

  /* live countdown to doors, then a ROOM OPEN readout through the night */
  function initDoorsCountdown(hero, sc) {
    var host = $('#doorsReadout', hero);
    if (!host) return;
    var out = $('b', host);
    if (!out) return;

    var openH = num(host.getAttribute('data-open-hour'), 19);
    var closeH = num(host.getAttribute('data-close-hour'), 2);
    var lblCount = host.getAttribute('data-label-countdown') || 'DOORS IN ';
    var lblOpen = host.getAttribute('data-label-open') || 'ROOM OPEN · ';
    var lblSeat = host.getAttribute('data-label-seating') || 'SEATING NOW';

    function tick() {
      var now = new Date();
      var target = new Date(now); target.setHours(openH, 0, 0, 0);
      var open = now.getHours() >= openH || now.getHours() < closeH;
      if (open) {
        out.parentNode.childNodes[0].nodeValue = lblOpen;
        out.textContent = lblSeat;
      } else {
        if (now > target) { target.setDate(target.getDate() + 1); }
        var d = Math.max(0, target - now), s = Math.floor(d / 1000);
        var hh = Math.floor(s / 3600), mm = Math.floor((s % 3600) / 60), ss = s % 60;
        out.parentNode.childNodes[0].nodeValue = lblCount;
        out.textContent = pad2(hh) + ':' + pad2(mm) + ':' + pad2(ss);
      }
    }
    tick();
    sc.iv(tick, 1000);
  }

  /* ---------- THE ROOM (photo showcase) ------------------------------------
     The plan is the navigator: hovering or tapping a zone swaps the picture in
     the panel. It advances on its own until the visitor interacts, and pauses
     while off-screen. The seat counts and capacity readout are gone — nothing
     here is invented any more, the merchant supplies each caption and photo. */
  function initFloorMap(floor, sc) {
    var zones = $$('.zone', floor);
    if (!zones.length) return;

    var card = $('#zoneCard', floor);
    var zcIdx = $('#zcIdx', floor), zcName = $('#zcName', floor), zcVibe = $('#zcVibe', floor);
    var shots = $$('.zc-shot', floor);
    var restIdx = zcIdx ? zcIdx.textContent : '';
    var restName = zcName ? zcName.textContent : '';
    var restVibe = zcVibe ? zcVibe.textContent : '';

    var current = -1;
    var taken = false;   /* the visitor has taken over */

    function show(i, fromUser) {
      if (fromUser) taken = true;
      i = ((i % zones.length) + zones.length) % zones.length;
      if (i === current) return;
      current = i;
      var z = zones[i];
      zones.forEach(function (o, n) { o.classList.toggle('warm', n === i); });
      shots.forEach(function (sh) {
        sh.classList.toggle('on', parseInt(sh.getAttribute('data-shot'), 10) === i);
      });
      if (card) card.classList.add('active');
      if (zcIdx) zcIdx.textContent = '/ ' + pad2(i + 1);
      if (zcName) zcName.textContent = z.getAttribute('data-name') || restName;
      if (zcVibe) {
        var cap = z.getAttribute('data-caption');
        zcVibe.textContent = cap && cap.length ? cap : restVibe;
      }
    }

    function rest() {
      if (taken) return;
      current = -1;
      zones.forEach(function (o) { o.classList.remove('warm'); });
      shots.forEach(function (sh) { sh.classList.remove('on'); });
      if (card) card.classList.remove('active');
      if (zcIdx) zcIdx.textContent = restIdx;
      if (zcName) zcName.textContent = restName;
      if (zcVibe) zcVibe.textContent = restVibe;
    }

    zones.forEach(function (z, i) {
      sc.on(z, 'mouseenter', function () { show(i, true); });
      sc.on(z, 'click', function () { show(i, true); });
      sc.on(z, 'focus', function () { show(i, true); });
    });
    /* leaving the plan altogether returns to the resting caption, but only
       while the visitor has not taken control */
    var plan = $('.floor-plan', floor);
    if (plan) sc.on(plan, 'mouseleave', function () { if (!taken) rest(); });

    /* ── auto-advance ── */
    var secs = num(card && card.getAttribute('data-autoplay'), 4);
    if (secs > 0 && shots.length > 1 && !RM) {
      var timer = null, visible = false;
      function tick() { if (!taken) show(current + 1, false); }
      function play() { if (timer || taken) return; timer = sc.iv(tick, secs * 1000); }
      function pause() { if (timer) { clearInterval(timer); timer = null; } }
      sc.io(new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          visible = e.isIntersecting;
          if (visible) { if (current < 0) show(0, false); play(); } else pause();
        });
      }, { threshold: 0.25 })).observe(floor);
      /* the first real interaction ends the carousel for good */
      sc.on(floor, 'pointerdown', pause);
      sc.on(floor, 'mouseenter', pause);
    }

    /* the signature ignition on scroll: zones light one by one */
    if (hasST && !RM) {
      var lit = -1;
      sc.st(ScrollTrigger.create({
        trigger: floor, start: 'top 62%', end: 'bottom 72%', scrub: false,
        onUpdate: function (self) {
          var n = clamp(Math.round(self.progress * zones.length), 0, zones.length);
          if (n === lit) return; lit = n;
          zones.forEach(function (z, i) { z.classList.toggle('lit', i < n); });
        }
      }));
    } else {
      zones.forEach(function (z) { z.classList.add('lit'); });
    }
  }

  /* ---------- NIGHT CLOCK + STEPS ------------------------------------------ */
  function initNight(night, sc) {
    var now = $('#nightNow', night);
    var prefix = (now && now.getAttribute('data-prefix')) || 'Montreal · ';
    function tick() {
      if (!now) return;
      var d = new Date();
      now.textContent = prefix + pad2(d.getHours()) + ':' + pad2(d.getMinutes()) + ':' + pad2(d.getSeconds());
    }
    tick();
    sc.iv(tick, 1000);

    var steps = $$('.n-step', night), fill = $('#nightFill', night);
    if (hasST && !RM) {
      steps.forEach(function (s) {
        sc.tw(gsap.to(s, {
          opacity: 1, y: 0, duration: .8, ease: 'power3.out',
          scrollTrigger: { trigger: s, start: 'top 88%', once: true, onEnter: function () { s.classList.add('on'); } }
        }));
      });
      if (fill) {
        var rail = $('.night-rail', night);
        sc.st(ScrollTrigger.create({
          trigger: rail, start: 'top 78%', end: 'bottom 60%', scrub: .6,
          onUpdate: function (self) { fill.style.width = (self.progress * 100) + '%'; }
        }));
      }
    } else {
      steps.forEach(function (s) { s.style.opacity = 1; s.style.transform = 'none'; s.classList.add('on'); });
      if (fill) fill.style.width = '100%';
    }
  }

  /* ---------- TABLE SET (pinned scrub choreography) ------------------------- */
  function initTableSet(pin, sc) {
    var root = (pin.closest && pin.closest('#tableset')) || pin;
    var chargers = $$('#chargers .charger', root), plates = $$('#plates .plate', root),
        glasses = $$('#glasses .glass', root), napkins = $$('#napkins .napkin', root),
        candles = $$('#candles .candle', root), table = $('#elTable', root), carafe = $('#elCarafe', root);
    var stageName = $('#tsStageName', root), progOut = $('#tsProgress', root);
    var footSteps = $$('.ts-foot .step', root);

    var names = footSteps.map(function (s) { return s.getAttribute('data-name'); }).filter(Boolean);
    if (!names.length) names = ['The Bare Table', 'The Settings', 'The Glasses', 'The Carafe', 'The Napkins', 'Candles Lit'];

    var scrubLen = num(pin.getAttribute('data-scrub-length'), 2600);
    if (scrubLen < 400) scrubLen = 2600;

    /* candle flicker, independent of the scrub */
    if (hasGSAP && !RM) {
      $$('#candles .flame', root).forEach(function (fl, i) {
        sc.tw(gsap.to(fl, {
          scaleY: 1.18, scaleX: 0.9, transformOrigin: '50% 100%', duration: .5 + Math.random() * 0.3,
          yoyo: true, repeat: -1, ease: 'sine.inOut', delay: i * 0.13
        }));
        sc.tw(gsap.to(fl, {
          opacity: 0.72, duration: .28 + Math.random() * 0.2, yoyo: true, repeat: -1, ease: 'sine.inOut', delay: i * 0.2
        }));
      });
    }

    function setStep(i) {
      footSteps.forEach(function (s) { s.classList.toggle('on', parseInt(s.getAttribute('data-step'), 10) <= i); });
      if (stageName) stageName.textContent = names[clamp(i, 0, names.length - 1)] || names[0];
    }

    if (hasST && !RM && !COARSE) {
      /* initial hidden states */
      gsap.set([].concat(chargers, plates, glasses, napkins), { opacity: 0 });
      gsap.set(chargers, { scale: 0.6, transformOrigin: '50% 50%' });
      gsap.set(plates, { scale: 0.4, transformOrigin: '50% 50%' });
      gsap.set(glasses, { y: -26, scale: 0.5, transformOrigin: '50% 50%' });
      gsap.set(napkins, { scale: 0.2, transformOrigin: '50% 50%' });
      gsap.set(candles, { opacity: 0, scale: 0.4, transformOrigin: '50% 100%' });
      gsap.set(carafe, { opacity: 0, y: -30, scale: 0.7, transformOrigin: '50% 60%' });
      gsap.set(table, { scale: 0.92, opacity: 0.5, transformOrigin: '50% 50%' });

      var mm = sc.mm(gsap.matchMedia());
      mm.add('(min-width:721px)', function () {
        var tl = gsap.timeline({
          scrollTrigger: {
            trigger: pin, start: 'top top', end: '+=' + scrubLen, scrub: .8, pin: true, anticipatePin: 1,
            onUpdate: function (self) { if (progOut) progOut.textContent = pad2(self.progress * 100) + ' %'; }
          }
        });
        tl.to(table, { scale: 1, opacity: 1, duration: 1.1, ease: 'power2.out' }, 0)
          .to(chargers, { opacity: 1, scale: 1, duration: 1.4, ease: 'back.out(1.5)', stagger: .14, onStart: function () { setStep(0); } }, 0.6)
          .to(plates, { opacity: 1, scale: 1, duration: 1.2, ease: 'back.out(1.7)', stagger: .12, onStart: function () { setStep(1); } }, 1.7)
          .to(glasses, { opacity: 1, y: 0, scale: 1, duration: 1.1, ease: 'back.out(2)', stagger: .12, onStart: function () { setStep(2); } }, 2.8)
          .to(carafe, { opacity: 1, y: 0, scale: 1, duration: 1.1, ease: 'back.out(1.6)', onStart: function () { setStep(3); } }, 3.9)
          .to(napkins, { opacity: 1, scale: 1, duration: 1, ease: 'back.out(1.8)', stagger: .1, onStart: function () { setStep(4); } }, 4.6)
          .to(candles, { opacity: 1, scale: 1, duration: 1.1, ease: 'back.out(1.7)', stagger: .16, onStart: function () { setStep(5); } }, 5.5);
        return function () { if (tl.scrollTrigger) tl.scrollTrigger.kill(true); tl.kill(); };
      });
      mm.add('(max-width:720px)', function () {
        /* mobile: no pin, reveal set on enter */
        var t = gsap.to([].concat([table], chargers, plates, glasses, [carafe], napkins, candles),
          { opacity: 1, scale: 1, y: 0, duration: .8, ease: 'power2.out', stagger: .03,
            scrollTrigger: { trigger: pin, start: 'top 70%', once: true,
              onEnter: function () { setStep(5); if (progOut) progOut.textContent = '100 %'; } } });
        return function () { if (t.scrollTrigger) t.scrollTrigger.kill(true); t.kill(); };
      });
    } else {
      /* reduced motion / no ScrollTrigger: show the table fully set */
      $$('.set-el', root).forEach(function (e) { e.style.opacity = 1; });
      setStep(5);
      if (progOut) progOut.textContent = '100 %';
    }
  }

  /* ---------- MAGNETIC BUTTON ---------------------------------------------- */
  function initMagnet(m, sc) {
    if (COARSE || RM || !hasGSAP) return;
    /* GSAP owns this element's transform outright — the button is laid out with
       flow, never centred with a translate, so nothing freezes a percentage into
       pixels when the webfont swaps in. Tweens are replaced on every move, so
       they are killed wholesale on unload rather than tracked one by one. */
    sc.on(m, 'mousemove', function (e) {
      var r = m.getBoundingClientRect();
      var x = e.clientX - r.left - r.width / 2, y = e.clientY - r.top - r.height / 2;
      gsap.to(m, { x: x * 0.3, y: y * 0.4, duration: .5, ease: 'power3.out' });
    });
    sc.on(m, 'mouseleave', function () {
      gsap.to(m, { x: 0, y: 0, duration: .6, ease: 'elastic.out(1,0.4)' });
    });
    sc.cleanup(function () { try { gsap.killTweensOf(m); } catch (e) {} });
  }

  /* ---------- FAQ ----------------------------------------------------------- */
  function initFaq(faq, sc) {
    var items = $$('.faq-item', faq);
    items.forEach(function (it) {
      var q = $('.faq-q', it), a = $('.faq-a', it);
      if (!q || !a) return;
      sc.on(q, 'click', function () {
        var open = it.classList.contains('open');
        items.forEach(function (o) {
          o.classList.remove('open');
          var oa = $('.faq-a', o), oq = $('.faq-q', o);
          if (oa) oa.style.maxHeight = null;
          if (oq) oq.setAttribute('aria-expanded', 'false');
        });
        if (!open) { it.classList.add('open'); a.style.maxHeight = a.scrollHeight + 'px'; q.setAttribute('aria-expanded', 'true'); }
      });
    });
  }

  /* ---------- FOOTER MARQUEE ------------------------------------------------ */
  function initMarquee(track, sc) {
    if (!hasGSAP || RM) return;
    var half = track.scrollWidth / 2;
    if (!half) return;
    sc.tw(gsap.to(track, {
      x: -half, duration: 22, ease: 'none', repeat: -1,
      modifiers: { x: function (x) { return (parseFloat(x) % half) + 'px'; } }
    }));
  }

  /* ---------- STICKY CTA DOCK ----------------------------------------------- */
  function initDock(d, sc) {
    function reveal() { d.classList.add('revealed'); }
    /* re-injected after boot: the intro is long gone, show it straight away */
    if (app.booted && app.preloaderDone) { reveal(); return; }

    var SEL = '#preloader,#intro,#introStatement,[id*="preload" i],[class*="preloader" i],[class*="intro-idx"],[class*="intro-col"],[class*="intro-grid"],.intro-statement,.d-veil,.hero-veil,.tex-veil,.pre-veil,[class*="veil"]';
    var pre = document.querySelector(SEL);
    if (pre) {
      var start = new Date().getTime();
      var iv = sc.iv(function () {
        var el = document.querySelector(SEL), gone = !el;
        if (el) {
          var cs = getComputedStyle(el);
          gone = (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.1);
        }
        if (gone || (new Date().getTime() - start) > 6000) { reveal(); clearInterval(iv); }
      }, 200);
    } else {
      if (document.readyState === 'complete') { sc.to(reveal, 700); }
      else { sc.on(window, 'load', function () { sc.to(reveal, 700); }); }
    }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     MODULE TABLE + SECTION-AWARE BOOT
     ══════════════════════════════════════════════════════════════════════ */

  var MODULES = [
    { sel: '#pre',            init: initPreloader },
    { sel: '#navDrawer',      init: initDrawer },
    { sel: '#hero',           init: initHero },
    { sel: '#arcStatement',   init: function (el, sc) { wordRise(el, sc); } },
    { sel: '#floor',          init: initFloorMap },
    { sel: '#night',          init: initNight },
    { sel: '#tsPin',          init: initTableSet },
    { sel: '#velvetQ',        init: function (el, sc) { wordRise(el, sc); } },
    { sel: '#magReserve',     init: initMagnet },
    { sel: '#spread',         init: initLightbox },
    { sel: '#faq',            init: initFaq },
    { sel: '#footTrack',      init: initMarquee },
    { sel: '.alia-cta-dock',  init: initDock }
  ];

  function sectionRoots(root) {
    if (root === document || root === document.body) {
      var all = $$('[id^="shopify-section-"]');
      return all.length ? all : [document.body];
    }
    return [root];
  }

  function initTree(root) {
    sectionRoots(root).forEach(function (sr) {
      var id = sr.id || '__page__';
      var sc = scopeFor(id);
      initReveals(sr, sc);
      MODULES.forEach(function (m) {
        qsaIn(sr, m.sel).forEach(function (el) {
          try { m.init(el, sc); } catch (e) {}
        });
      });
    });
  }

  function refreshST(delay) {
    if (!hasST) return;
    if (delay) setTimeout(function () { ScrollTrigger.refresh(); }, delay);
    else ScrollTrigger.refresh();
  }

  /* ---- boot ---- */
  initSmoothScroll();   /* must be first */
  initAnchors();
  initNavProgress();
  initCursor();
  initEasterEgg();
  initTree(document);
  app.booted = true;

  /* ---- theme editor lifecycle ---- */
  document.addEventListener('shopify:section:load', function (e) {
    var id = e.target && e.target.id ? e.target.id : null;
    if (id) destroyScope(id);
    initTree(e.target);
    rebindChrome();            /* #nav / #prog may have just been replaced */
    refreshST();               /* pin spacing after injection or reorder */
    refreshST(260);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var id = e.target && e.target.id ? e.target.id : null;
    if (id) destroyScope(id);
    if (app.heroStart && id && !document.getElementById('hero')) app.heroStart = null;
    rebindChrome();
    refreshST();
  });

  document.addEventListener('shopify:section:reorder', function () { rebindChrome(); refreshST(); });
  document.addEventListener('shopify:section:select', function () { refreshST(); });
  document.addEventListener('shopify:section:deselect', function () { refreshST(); });
  document.addEventListener('shopify:block:select', function () { refreshST(); });
  document.addEventListener('shopify:block:deselect', function () { refreshST(); });

  /* ---- refresh on fonts + load ---- */
  if (hasST) {
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(function () { ScrollTrigger.refresh(); }); }
    window.addEventListener('load', function () { setTimeout(function () { ScrollTrigger.refresh(); }, 260); });
    setTimeout(function () { ScrollTrigger.refresh(); }, 700);
  }
})();
